import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/eoffice/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KpiStrip } from "@/components/eoffice/analytics/KpiStrip";
import { Insights, buildInsights } from "@/components/eoffice/analytics/Insights";
import { TrendChart, TurnaroundChart, DepartmentChart, CategoryChart } from "@/components/eoffice/analytics/Charts";
import { PendingByHolder, StuckFiles, StaffResponse } from "@/components/eoffice/analytics/Tables";

const RANGES = [{ d: 7, l: "7D" }, { d: 30, l: "30D" }, { d: 90, l: "90D" }, { d: 365, l: "12M" }];

function RangePicker({ days, setDays }) {
  return (
    <div className="flex border border-[#DCD9D0] bg-white" data-testid="range-picker">
      {RANGES.map((r) => (
        <button
          key={r.d}
          data-testid={`range-${r.l.toLowerCase()}`}
          onClick={() => setDays(r.d)}
          className={`px-4 h-9 font-mono-eo text-[12px] tracking-wider border-l first:border-l-0 border-[#DCD9D0] transition-colors duration-150 ${days === r.d ? "bg-[#1D3557] text-white" : "text-[#5C5C54] hover:bg-[#FBFAF3]"}`}
        >{r.l}</button>
      ))}
    </div>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params.get("new") === "1") nav("/files/my?new=1", { replace: true });
  }, [params, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/analytics?days=${days}`);
      setData(data);
    } catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <Layout>
      <div className="px-6 md:px-10 py-10 space-y-6" data-testid="analytics-dashboard">
        <div className="flex flex-wrap items-end gap-4 border-b border-[#DCD9D0] pb-6">
          <div className="min-w-0">
            <div className="font-mono-eo text-[10px] uppercase tracking-[0.3em] text-[#8A8A80]">// Management overview</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-none mt-2">Dashboard</h1>
            <p className="text-[14px] text-[#5C5C54] mt-3">Welcome back, <b className="text-[#0A0A0A]">{user?.name}</b>. Insights across every file you're part of.</p>
          </div>
          <div className="flex-1" />
          <RangePicker days={days} setDays={setDays} />
          <button data-testid="analytics-refresh" onClick={load} title="Refresh" className="eo-btn-ghost h-9 flex items-center"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></button>
          <button data-testid="analytics-create-file" onClick={() => nav("/files/my?new=1")} className="eo-btn-primary h-9 flex items-center gap-1.5"><Plus size={14} /> New file</button>
        </div>

        {!data ? (
          <div className="h-[300px] bg-white border border-[#DCD9D0] animate-pulse" data-testid="analytics-loading" />
        ) : (
          <div className={`space-y-6 transition-opacity duration-200 ${loading ? "opacity-60" : ""}`}>
            <KpiStrip k={data.kpis} />
            <Insights items={buildInsights(data)} />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <TrendChart data={data.trend} gran={data.granularity} />
              <TurnaroundChart data={data.turnaround_distribution} />
              <StuckFiles rows={data.stuck_files} />
              <PendingByHolder rows={data.pending_by_holder} />
              <DepartmentChart data={data.departments} />
              <CategoryChart data={data.categories} />
              <StaffResponse rows={data.staff_response} />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
